import { doodBlockConfig, propertyValue } from "./dood-block-config";
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from "./descriptor-field-order";
import type { IBlockDescriptor } from "./descriptor";
import { EMPTY_SPACING_BOX } from "./spacing-box";

/** Length units without `auto` — for grid gaps, padding, sizes, etc. */
const LENGTH_UNIT_CHOICES = [
  { value: "px", label: "pages.blocks.frame.size.units.px" },
  { value: "%", label: "pages.blocks.frame.size.units.percent" },
  { value: "rem", label: "pages.blocks.frame.size.units.rem" },
  { value: "vw", label: "pages.blocks.frame.size.units.vw" },
  { value: "vh", label: "pages.blocks.frame.size.units.vh" },
] as const;

/** Margin units — `auto` enables horizontal centering (left + right). */
const MARGIN_UNIT_CHOICES = [
  {
    value: "auto",
    label: "pages.blocks.frame.margin.units.auto",
    inputDisabled: true,
  },
  ...LENGTH_UNIT_CHOICES,
] as const;

const spacingBox = (
  labelKey: string,
  helpKey: string,
  sideLabelPrefix: string,
  choices: readonly {
    value: string;
    label: string;
    inputDisabled?: boolean;
  }[],
) =>
  propertyValue({
    type: "spacing_box",
    value: {
      top: { ...EMPTY_SPACING_BOX.top },
      right: { ...EMPTY_SPACING_BOX.right },
      bottom: { ...EMPTY_SPACING_BOX.bottom },
      left: { ...EMPTY_SPACING_BOX.left },
    },
    options: {
      label: labelKey,
      help: helpKey,
      min: 0,
      sideLabelPrefix,
      choices: [...choices],
    },
  });

const lengthUnitValue = (labelKey: string, helpKey: string) =>
  propertyValue({
    type: "unit_value",
    value: { unit: "px", value: 0 },
    options: {
      label: labelKey,
      help: helpKey,
      min: 0,
      choices: [...LENGTH_UNIT_CHOICES],
    },
  });

/** Size with Auto = hug content (`width` / `height: auto`). */
const sizeUnitValue = (
  labelKey: string,
  helpKey: string,
  value: { unit: string; value: number } = { unit: "auto", value: 0 },
) =>
  propertyValue({
    type: "unit_value",
    value,
    options: {
      label: labelKey,
      help: helpKey,
      min: 0,
      choices: [
        {
          value: "auto",
          label: "pages.blocks.frame.size.units.auto",
          inputDisabled: true,
        },
        ...LENGTH_UNIT_CHOICES,
      ],
    },
  });

export const FRAMEBLOCK_LEGACY_FIELD_ORDER = [
  "body",
  { type: "separator" },
  "width",
  "height",
  "layout",
  "alignment",
  "gap",
  "columns",
  "column_gap",
  "row_gap",
  { type: "separator" },
  "padding",
  "margin",
  { type: "separator" },
  "background_color",
  "background_image",
  "background_repeat",
  { type: "separator" },
  "show_stroke",
  "stroke_color",
  "stroke_width",
  { type: "separator" },
  "show_drop_shadow",
  "drop_shadow_color",
  "drop_shadow_offset_x",
  "drop_shadow_offset_y",
  "drop_shadow_blur",
  "drop_shadow_spread",
  { type: "separator" },
  "border_radius",
  { type: "separator" },
  "custom_css",
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  frameBlockDescriptor: _generatedFrameBlockDescriptor,
  frameBlockBlocDefinition,
  frameBlockDashboardLayout,
  frameBlockConfig,
  frameBlockResolveConfig,
  frameBlockProperties,
} = doodBlockConfig(
  {
    name: "Frame",
    componentType: "FrameBlockComponent",
    category: "layout",
    description: "Container frame with flexible layout, spacing and background.",
    keywords: [
      "frame",
      "container",
      "layout",
      "flex",
      "grid",
      "padding",
      "margin",
      "stroke",
      "border",
      "radius",
      "shadow",
      "css",
    ],
    sharedFields: {},
    slots: [
      {
        key: "body",
        name: "pages.blocks.frame.body.label",
        // Open slot: the Studio picker resolves allowed children from the bloc
        // registry (`accept: "all"`), excluding page roots and `hideFromPicker`.
        accept: "all",
        slotItemFormat: "ids",
      },
    ],
  },

  () => ({
    width: sizeUnitValue(
      "pages.blocks.frame.width.label",
      "pages.blocks.frame.width.help",
      { unit: "%", value: 100 },
    ),
    height: sizeUnitValue(
      "pages.blocks.frame.height.label",
      "pages.blocks.frame.height.help",
    ),
    layout: propertyValue({
      type: "select",
      value: "vertical",
      options: {
        label: "pages.blocks.frame.layout.label",
        help: "pages.blocks.frame.layout.help",
        choices: [
          {
            value: "vertical",
            label: "pages.blocks.frame.layout.options.vertical",
          },
          {
            value: "horizontal",
            label: "pages.blocks.frame.layout.options.horizontal",
          },
          {
            value: "grid",
            label: "pages.blocks.frame.layout.options.grid",
          },
        ],
      },
    }),
    alignment: propertyValue({
      type: "select",
      value: "top-left",
      options: {
        label: "pages.blocks.frame.alignment.label",
        help: "pages.blocks.frame.alignment.help",
        choices: [
          {
            value: "top-left",
            label: "pages.blocks.frame.alignment.options.top-left",
          },
          {
            value: "top-center",
            label: "pages.blocks.frame.alignment.options.top-center",
          },
          {
            value: "top-right",
            label: "pages.blocks.frame.alignment.options.top-right",
          },
          {
            value: "center-left",
            label: "pages.blocks.frame.alignment.options.center-left",
          },
          {
            value: "center",
            label: "pages.blocks.frame.alignment.options.center",
          },
          {
            value: "center-right",
            label: "pages.blocks.frame.alignment.options.center-right",
          },
          {
            value: "bottom-left",
            label: "pages.blocks.frame.alignment.options.bottom-left",
          },
          {
            value: "bottom-center",
            label: "pages.blocks.frame.alignment.options.bottom-center",
          },
          {
            value: "bottom-right",
            label: "pages.blocks.frame.alignment.options.bottom-right",
          },
        ],
      },
    }),
    gap: propertyValue({
      type: "unit_value",
      value: { unit: "px", value: 0 },
      options: {
        label: "pages.blocks.frame.gap.label",
        help: "pages.blocks.frame.gap.help",
        min: 0,
        choices: [
          {
            value: "space-between",
            label: "pages.blocks.frame.gap.units.space-between",
            inputDisabled: true,
          },
          {
            value: "space-around",
            label: "pages.blocks.frame.gap.units.space-around",
            inputDisabled: true,
          },
          {
            value: "space-evenly",
            label: "pages.blocks.frame.gap.units.space-evenly",
            inputDisabled: true,
          },
          ...LENGTH_UNIT_CHOICES,
        ],
      },
    }),
    columns: propertyValue({
      type: "number",
      value: 2,
      options: {
        label: "pages.blocks.frame.columns.label",
        help: "pages.blocks.frame.columns.help",
        min: 1,
        max: 12,
      },
    }),
    rows: propertyValue({
      type: "number",
      value: 1,
      options: {
        label: "pages.blocks.frame.rows.label",
        help: "pages.blocks.frame.rows.help",
        min: 1,
        max: 12,
      },
    }),
    column_gap: lengthUnitValue(
      "pages.blocks.frame.column-gap.label",
      "pages.blocks.frame.column-gap.help",
    ),
    row_gap: lengthUnitValue(
      "pages.blocks.frame.row-gap.label",
      "pages.blocks.frame.row-gap.help",
    ),
    padding: spacingBox(
      "pages.blocks.frame.padding.label",
      "pages.blocks.frame.padding.help",
      "pages.blocks.frame.padding",
      LENGTH_UNIT_CHOICES,
    ),
    margin: spacingBox(
      "pages.blocks.frame.margin.label",
      "pages.blocks.frame.margin.help",
      "pages.blocks.frame.margin",
      MARGIN_UNIT_CHOICES,
    ),
    background_color: propertyValue({
      type: "color",
      value: { type: "hex", value: "transparent" },
      options: {
        label: "pages.blocks.frame.background-color.label",
      },
    }),
    background_image: propertyValue({
      type: "image",
      value: "",
      options: {
        label: "pages.blocks.frame.background-image.label",
        help: "pages.blocks.frame.background-image.help",
      },
    }),
    background_repeat: propertyValue({
      type: "select",
      value: "no-repeat",
      options: {
        label: "pages.blocks.frame.background-repeat.label",
        help: "pages.blocks.frame.background-repeat.help",
        choices: [
          {
            value: "no-repeat",
            label: "pages.blocks.frame.background-repeat.options.no-repeat",
          },
          {
            value: "repeat",
            label: "pages.blocks.frame.background-repeat.options.repeat",
          },
          {
            value: "repeat-x",
            label: "pages.blocks.frame.background-repeat.options.repeat-x",
          },
          {
            value: "repeat-y",
            label: "pages.blocks.frame.background-repeat.options.repeat-y",
          },
          {
            value: "space",
            label: "pages.blocks.frame.background-repeat.options.space",
          },
          {
            value: "round",
            label: "pages.blocks.frame.background-repeat.options.round",
          },
        ],
      },
    }),
    show_stroke: propertyValue({
      type: "checkbox",
      value: false,
      options: {
        label: "pages.blocks.frame.show-stroke.label",
        help: "pages.blocks.frame.show-stroke.help",
      },
    }),
    stroke_color: propertyValue({
      type: "color",
      value: { type: "hex", value: "#000000" },
      options: {
        label: "pages.blocks.frame.stroke-color.label",
      },
    }),
    stroke_width: propertyValue({
      type: "number",
      value: 1,
      options: {
        label: "pages.blocks.frame.stroke-width.label",
        help: "pages.blocks.frame.stroke-width.help",
        min: 0,
      },
    }),
    show_drop_shadow: propertyValue({
      type: "checkbox",
      value: false,
      options: {
        label: "pages.blocks.frame.show-drop-shadow.label",
        help: "pages.blocks.frame.show-drop-shadow.help",
      },
    }),
    drop_shadow_color: propertyValue({
      type: "color",
      value: { type: "hex", value: "#00000040" },
      options: {
        label: "pages.blocks.frame.drop-shadow-color.label",
      },
    }),
    drop_shadow_offset_x: propertyValue({
      type: "number",
      value: 0,
      options: {
        label: "pages.blocks.frame.drop-shadow-offset-x.label",
        help: "pages.blocks.frame.drop-shadow-offset-x.help",
      },
    }),
    drop_shadow_offset_y: propertyValue({
      type: "number",
      value: 4,
      options: {
        label: "pages.blocks.frame.drop-shadow-offset-y.label",
        help: "pages.blocks.frame.drop-shadow-offset-y.help",
      },
    }),
    drop_shadow_blur: propertyValue({
      type: "number",
      value: 8,
      options: {
        label: "pages.blocks.frame.drop-shadow-blur.label",
        help: "pages.blocks.frame.drop-shadow-blur.help",
        min: 0,
      },
    }),
    drop_shadow_spread: propertyValue({
      type: "number",
      value: 0,
      options: {
        label: "pages.blocks.frame.drop-shadow-spread.label",
        help: "pages.blocks.frame.drop-shadow-spread.help",
      },
    }),
    border_radius: lengthUnitValue(
      "pages.blocks.frame.border-radius.label",
      "pages.blocks.frame.border-radius.help",
    ),
    custom_css: propertyValue({
      type: "textarea",
      value: "",
      options: {
        label: "pages.blocks.frame.custom-css.label",
        help: "pages.blocks.frame.custom-css.help",
        placeholder: "pages.blocks.frame.custom-css.placeholder",
        rows: 8,
        monospace: true,
      },
    }),
  }),

  () => ({
    omitFields: ["rows"] as const,
    tabs: [
      {
        label: "pages.blocks.frame.tab.content.label",
        fieldsets: [
          {
            legend: "pages.blocks.frame.fieldset.content.label",
            fields: ["body"],
          },
          {
            legend: "pages.blocks.frame.fieldset.layout.label",
            fields: [
              { prop: "width" as const, width: "50%" },
              { prop: "height" as const, width: "50%" },
              "layout",
              "alignment",
              {
                prop: "gap" as const,
                visibleWhen: {
                  anyOf: [
                    { layout: "vertical" as const },
                    { layout: "horizontal" as const },
                  ],
                },
              },
              {
                prop: "columns" as const,
                visibleWhen: { layout: "grid" as const },
              },
              {
                prop: "column_gap" as const,
                visibleWhen: { layout: "grid" as const },
              },
              {
                prop: "row_gap" as const,
                visibleWhen: { layout: "grid" as const },
              },
              "padding",
              "margin",
            ],
          },
          {
            legend: "pages.blocks.frame.fieldset.background.label",
            fields: [
              "background_color",
              "background_image",
              {
                prop: "background_repeat" as const,
                visibleWhen: {
                  prop: "background_image" as const,
                  nonEmpty: true,
                },
              },
            ],
          },
          {
            legend: "pages.blocks.frame.fieldset.corners.label",
            fields: ["border_radius"],
          },
          {
            legend: "pages.blocks.frame.fieldset.stroke.label",
            fields: [
              "show_stroke",
              {
                prop: "stroke_color" as const,
                visibleWhen: { show_stroke: true as const },
              },
              {
                prop: "stroke_width" as const,
                visibleWhen: { show_stroke: true as const },
              },
            ],
          },
          {
            legend: "pages.blocks.frame.fieldset.drop-shadow.label",
            fields: [
              "show_drop_shadow",
              {
                prop: "drop_shadow_color" as const,
                visibleWhen: { show_drop_shadow: true as const },
              },
              {
                prop: "drop_shadow_offset_x" as const,
                visibleWhen: { show_drop_shadow: true as const },
              },
              {
                prop: "drop_shadow_offset_y" as const,
                visibleWhen: { show_drop_shadow: true as const },
              },
              {
                prop: "drop_shadow_blur" as const,
                visibleWhen: { show_drop_shadow: true as const },
              },
              {
                prop: "drop_shadow_spread" as const,
                visibleWhen: { show_drop_shadow: true as const },
              },
            ],
          },
          {
            legend: "pages.blocks.frame.fieldset.custom-css.label",
            fields: ["custom_css"],
          },
        ],
      },
    ],
  }),
);

export function frameBlockDescriptor(): IBlockDescriptor;
export function frameBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function frameBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedFrameBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(
    _generatedFrameBlockDescriptor,
    frameBlockProperties,
    fieldOrder,
  );
}

export {
  frameBlockBlocDefinition,
  frameBlockDashboardLayout,
  frameBlockProperties,
  frameBlockConfig,
  frameBlockResolveConfig,
};

export type FrameBlockConfig = typeof frameBlockConfig;
